// Mini Crossword generator: standard crossing placement on a small square grid, seeded.
// Falls back to fewer words when nothing more fits.

import { shuffle, type Rng } from '@kit/rng';
import { CROSSWORDS, bandFor } from '@content/words/crossword-words';
import { CLUE_WORDS } from '@content/words/clues';

export type Dir = 'across' | 'down';
export interface Placed { word: string; clue: string; row: number; col: number; dir: Dir; num: number }
export interface Puzzle { size: number; words: Placed[]; grid: (string | null)[][] }

export interface PuzzleSpec { size: number; target: number; maxLen: number }
export function specFor(level: number): PuzzleSpec {
  if (level <= 2) return { size: 3, target: 3, maxLen: 3 };
  if (level <= 6) return { size: 5, target: 5, maxLen: 5 };
  return { size: 7, target: 8, maxLen: 7 };
}

export interface Candidate { word: string; clue: string }

export function candidates(level: number): Candidate[] {
  const band = bandFor(level);
  const spec = specFor(level);
  const out: Candidate[] = [];
  const seen = new Set<string>();
  for (const c of CROSSWORDS) {
    if (c.band > band || c.word.length > spec.maxLen || c.word.length < 3) continue;
    if (band === 1 && c.word.length !== 3) continue;
    if (!seen.has(c.word)) { seen.add(c.word); out.push({ word: c.word, clue: c.clue }); }
  }
  if (band >= 2) {
    const clueIdx = level <= 4 ? 0 : level <= 7 ? 1 : 3;
    for (const w of CLUE_WORDS) {
      if (w.word.length > spec.maxLen || w.word.length < 3 || !/^[a-z]+$/.test(w.word) || seen.has(w.word)) continue;
      if (w.tier > (level <= 6 ? 2 : 3)) continue;
      seen.add(w.word);
      out.push({ word: w.word, clue: w.clues[Math.min(clueIdx, w.clues.length - 1)] });
    }
  }
  return out;
}

function emptyGrid(n: number): (string | null)[][] { return Array.from({ length: n }, () => Array<string | null>(n).fill(null)); }

function canPlace(grid: (string | null)[][], word: string, row: number, col: number, dir: Dir, requireCross: boolean): boolean {
  const n = grid.length;
  const dr = dir === 'down' ? 1 : 0, dc = dir === 'across' ? 1 : 0;
  const endR = row + dr * (word.length - 1), endC = col + dc * (word.length - 1);
  if (row < 0 || col < 0 || endR >= n || endC >= n) return false;
  // cell before and after must be empty
  const br = row - dr, bc = col - dc, ar = endR + dr, ac = endC + dc;
  if (br >= 0 && bc >= 0 && br < n && bc < n && grid[br][bc] !== null) return false;
  if (ar >= 0 && ac >= 0 && ar < n && ac < n && grid[ar][ac] !== null) return false;
  let crosses = 0;
  for (let i = 0; i < word.length; i++) {
    const r = row + dr * i, c = col + dc * i;
    const cell = grid[r][c];
    if (cell !== null) {
      if (cell !== word[i]) return false;
      crosses++;
    } else {
      // perpendicular neighbors must be empty when this is a fresh cell
      const pr1 = r + dc, pc1 = c + dr, pr2 = r - dc, pc2 = c - dr;
      if (pr1 < n && pc1 < n && grid[pr1][pc1] !== null) return false;
      if (pr2 >= 0 && pc2 >= 0 && grid[pr2][pc2] !== null) return false;
    }
  }
  if (requireCross && crosses === 0) return false;
  return true;
}

function place(grid: (string | null)[][], word: string, row: number, col: number, dir: Dir) {
  const dr = dir === 'down' ? 1 : 0, dc = dir === 'across' ? 1 : 0;
  for (let i = 0; i < word.length; i++) grid[row + dr * i][col + dc * i] = word[i];
}

export function makePuzzle(level: number, rng: Rng): Puzzle {
  const spec = specFor(level);
  const pool = shuffle(rng, candidates(level));
  let best: Puzzle | null = null;
  for (let attempt = 0; attempt < 12 && (!best || best.words.length < spec.target); attempt++) {
    const grid = emptyGrid(spec.size);
    const placed: Placed[] = [];
    const words = shuffle(rng, pool);
    // first word: longest that fits, across, centered
    const first = words.find(w => w.word.length <= spec.size) ;
    if (!first) break;
    const r0 = Math.floor(spec.size / 2), c0 = Math.floor((spec.size - first.word.length) / 2);
    place(grid, first.word, r0, c0, 'across');
    placed.push({ word: first.word, clue: first.clue, row: r0, col: c0, dir: 'across', num: 0 });
    const used = new Set([first.word]);
    let stall = 0;
    while (placed.length < spec.target && stall < 2) {
      let added = false;
      for (const cand of words) {
        if (used.has(cand.word) || cand.word.length > spec.size) continue;
        // try every crossing with every placed letter
        const options: { row: number; col: number; dir: Dir }[] = [];
        for (const p of placed) {
          const pdr = p.dir === 'down' ? 1 : 0, pdc = p.dir === 'across' ? 1 : 0;
          for (let i = 0; i < p.word.length; i++) {
            const r = p.row + pdr * i, c = p.col + pdc * i;
            for (let j = 0; j < cand.word.length; j++) {
              if (cand.word[j] !== p.word[i]) continue;
              const dir: Dir = p.dir === 'across' ? 'down' : 'across';
              const row = dir === 'down' ? r - j : r, col = dir === 'across' ? c - j : c;
              if (canPlace(grid, cand.word, row, col, dir, true)) options.push({ row, col, dir });
            }
          }
        }
        if (options.length) {
          const o = options[Math.floor(rng() * options.length)];
          place(grid, cand.word, o.row, o.col, o.dir);
          placed.push({ word: cand.word, clue: cand.clue, row: o.row, col: o.col, dir: o.dir, num: 0 });
          used.add(cand.word);
          added = true;
          if (placed.length >= spec.target) break;
        }
      }
      if (!added) stall++;
    }
    const puzzle: Puzzle = { size: spec.size, words: placed, grid };
    if (!best || placed.length > best.words.length) best = puzzle;
  }
  const p = best!;
  // number the words top-left to bottom-right like a real crossword
  const starts = new Map<string, number>();
  let n = 1;
  for (let r = 0; r < p.size; r++) for (let c = 0; c < p.size; c++) {
    if (p.words.some(w => w.row === r && w.col === c)) { starts.set(`${r},${c}`, n++); }
  }
  for (const w of p.words) w.num = starts.get(`${w.row},${w.col}`)!;
  p.words.sort((a, b) => a.num - b.num || (a.dir === 'across' ? -1 : 1));
  return p;
}

/** every maximal run of 2+ letters must be exactly one placed word (no accidental words) */
export function validate(p: Puzzle): { ok: boolean; reason?: string } {
  const n = p.size;
  const readable = new Set(p.words.map(w => `${w.row},${w.col},${w.dir},${w.word}`));
  for (const w of p.words) {
    const dr = w.dir === 'down' ? 1 : 0, dc = w.dir === 'across' ? 1 : 0;
    for (let i = 0; i < w.word.length; i++) {
      const cell = p.grid[w.row + dr * i]?.[w.col + dc * i];
      if (cell !== w.word[i]) return { ok: false, reason: `${w.word} not readable` };
    }
  }
  for (const dir of ['across', 'down'] as Dir[]) {
    for (let a = 0; a < n; a++) {
      let run = '', start = -1;
      for (let b = 0; b <= n; b++) {
        const r = dir === 'across' ? a : b, c = dir === 'across' ? b : a;
        const cell = b < n ? p.grid[r][c] : null;
        if (cell) { if (!run) start = b; run += cell; }
        else {
          if (run.length >= 2) {
            const row = dir === 'across' ? a : start, col = dir === 'across' ? start : a;
            if (!readable.has(`${row},${col},${dir},${run}`)) return { ok: false, reason: `stray run ${run}` };
          }
          run = '';
        }
      }
    }
  }
  return { ok: true };
}
